import React, { ReactElement } from 'react'
import { observer } from 'mobx-react'
import { PayoutEntryHedge } from '../../../state/Radom'
import { formatAssetAmount, trimZeros } from '../../../util/Util'
import DetailsRow from './DetailsRow'
import { computeHedgedPayoutCosts, formatFiatCost, withFiatCost } from './hedgedPayoutCosts'

const formatBps = (value: number): string => `${trimZeros(value.toFixed(2))} bps`

const formatPercent = (value: number): string => `${trimZeros(value.toFixed(4))}%`

export const HedgedPayoutCostBreakdown = observer(({
  hedge,
  cryptoTicker,
  compact = false
}: {
  hedge: PayoutEntryHedge
  cryptoTicker: string
  compact?: boolean
}): ReactElement => {
  const fundingTicker = hedge.fundingCurrency
  const costs = computeHedgedPayoutCosts(hedge)

  const rows: Array<{ title: string, value: string, nested?: boolean }> = [
    {
      title: 'Market mid price',
      value: `1 ${cryptoTicker} = ${formatAssetAmount(costs.midPrice, fundingTicker)} ${fundingTicker}`
    },
    {
      title: 'Locked rate',
      value: `1 ${cryptoTicker} = ${formatAssetAmount(costs.lockedRate, fundingTicker)} ${fundingTicker}`
    },
    {
      title: 'Rate markup',
      value: withFiatCost(formatBps(costs.markupBps), costs.rateCost),
      nested: true
    },
    {
      title: 'Conversion amount',
      value: `${formatAssetAmount(costs.stableForTrade, fundingTicker)} ${fundingTicker}`
    },
    {
      title: 'Conversion fee',
      value: withFiatCost(
        `${formatAssetAmount(costs.conversionFee, undefined, 6)} ${fundingTicker} (${formatPercent(costs.conversionFeePercent)})`,
        costs.conversionFeeCost
      )
    },
    {
      title: 'Network fee buffer',
      value: withFiatCost(
        `${formatAssetAmount(costs.feeBuffer, cryptoTicker)} ${cryptoTicker}`,
        costs.feeBufferCost
      )
    },
    {
      title: 'Provider withdrawal fee',
      value: withFiatCost(
        `${formatAssetAmount(costs.providerWithdrawalFee, cryptoTicker)} ${cryptoTicker}`,
        costs.providerFeeCost
      )
    }
  ]

  if (compact) {
    return (
      <>
        {rows.map(row => (
          <DetailsRow key={row.title} title={row.value && row.nested ? `  ${row.title}` : row.title} value={row.value} />
        ))}
        {costs.totalCost != null && (
          <DetailsRow title="Total cost vs market rate" value={`≈ ${formatFiatCost(costs.totalCost)}`} />
        )}
      </>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map(row => (
        <div
          key={row.title}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            paddingLeft: row.nested ? 10 : 0
          }}
        >
          <span style={{ color: row.nested ? '#8a909c' : '#5f6675', fontSize: row.nested ? 12 : 12.5 }}>
            {row.title}
          </span>
          <span style={{
            color: '#20242d',
            fontSize: row.nested ? 12 : 13,
            fontWeight: row.nested ? 400 : 500,
            textAlign: 'right'
          }}>
            {row.value}
          </span>
        </div>
      ))}
      {costs.totalCost != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ color: '#5f6675', fontSize: 12.5 }}>Total cost vs market rate</span>
          <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500, textAlign: 'right' }}>
            ≈ {formatFiatCost(costs.totalCost)}
          </span>
        </div>
      )}
    </div>
  )
})

export default HedgedPayoutCostBreakdown
